'use client';

import Skeleton from '@pixum/skeleton';
import Text, { TextTag, type TextVariant } from '@pixum/text';
import { formatPrice, type LocaleIso } from 'utils';
import { useCartStore, type CartStore } from 'stores/cart';
import styles from './CartPrice.module.css';

export type CartPriceSlot = 'cartValue' | 'shippingCost' | 'total';

export interface CartPriceProps {
  /** RSC/SSR formatted value — shown when cartLiveValues is undefined */
  value: string;
  /** Typography variant — wraps in <Text> when provided */
  variant?: TextVariant;
  /** Scope to one article — shows liveValues.articlePrices[articleId] when defined */
  articleId?: number;
  /** Formatted live article value when the article total needs additional detail prices. */
  liveValue?: string;
  /** Which summary field to read from cartLiveValues */
  priceSlot?: CartPriceSlot;
  /** Locale for formatting raw numbers from cartLiveValues */
  locale: LocaleIso;
  /** Layout overrides (min-width, text-align) */
  className?: string;
}

export function CartPrice({ value, variant, articleId, liveValue, priceSlot, locale, className }: CartPriceProps) {
  const liveValues = useCartStore((s: CartStore) => s.cartLiveValues);
  const pendingArticle = useCartStore((s: CartStore) => s.pendingArticle);

  // pendingArticle being set is the single source of truth for an in-flight
  // quantity mutation — no separate isBusy subscription needed here.
  const isQuantityMutation = pendingArticle?.topic === 'quantity';

  // Article card: skeleton for the specific article being mutated.
  const isArticlePending = articleId !== undefined && isQuantityMutation && pendingArticle?.articleId === articleId;
  // Summary rows: skeleton for all totals during any quantity mutation.
  const isSummaryPending = priceSlot !== undefined && isQuantityMutation;

  let displayValue = value;

  if (liveValues !== undefined) {
    const { articlePrices, totalGross, totalArticleSumGross, totalSumDeliveryGross } = liveValues;

    if (articleId !== undefined && articlePrices[articleId] !== undefined) {
      displayValue = liveValue ?? formatPrice(articlePrices[articleId], locale) ?? value;
    } else if (priceSlot === 'cartValue') {
      displayValue = formatPrice(totalArticleSumGross, locale) ?? value;
    } else if (priceSlot === 'shippingCost') {
      displayValue = formatPrice(totalSumDeliveryGross, locale) ?? value;
    } else if (priceSlot === 'total') {
      displayValue = formatPrice(totalGross, locale) ?? value;
    }
  }

  const content = variant ? (
    <Text variant={variant} tag={TextTag.SPAN}>
      {displayValue}
    </Text>
  ) : (
    displayValue
  );

  if (isArticlePending || isSummaryPending) {
    return (
      <span className={`${styles['wrapper']} ${className ?? ''}`.trim()}>
        <span className={styles['text--hidden']}>{content}</span>
        <Skeleton className={styles['skeleton']} aria-hidden="true" />
      </span>
    );
  }

  return <span className={className}>{content}</span>;
}
